export const IMAGE_COMPRESSION_THRESHOLD_BYTES = 1536 * 1024;
export const IMAGE_COMPRESSION_TARGET_BYTES = 2 * 1024 * 1024;
export const IMAGE_COMPRESSION_MAX_DIMENSION = 1800;

const IMAGE_NAME_PATTERN = /\.(avif|bmp|gif|heic|heif|ico|jpe?g|png|tiff?|webp)$/i;

export const shouldTryImageCompression = (file) => {
  if (!file) return false;
  if (typeof file.size === "number" && file.size <= IMAGE_COMPRESSION_THRESHOLD_BYTES) {
    return false;
  }

  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "");
  return type.startsWith("image/") || IMAGE_NAME_PATTERN.test(name);
};

const loadImageFromObjectUrl = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const fileFromBlob = (blob, file) => {
  const originalName = String(file.name || "upload.jpg");
  const nextName = originalName.replace(/\.[^.]+$/, "") || "upload";
  if (typeof File === "function") {
    return new File([blob], `${nextName}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  }
  blob.name = `${nextName}.jpg`;
  return blob;
};

export const compressImageForUpload = async (
  file,
  {
    maxDimension = IMAGE_COMPRESSION_MAX_DIMENSION,
    targetBytes = IMAGE_COMPRESSION_TARGET_BYTES,
    qualities = [0.82, 0.74, 0.66],
  } = {}
) => {
  if (!shouldTryImageCompression(file)) return file;
  if (
    typeof document === "undefined" ||
    typeof Image === "undefined" ||
    typeof URL === "undefined" ||
    typeof URL.createObjectURL !== "function"
  ) {
    return file;
  }

  let objectUrl;
  try {
    objectUrl = URL.createObjectURL(file);
    const image = await loadImageFromObjectUrl(objectUrl);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (!width || !height) return file;

    const scale = Math.min(1, maxDimension / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));

    const context = canvas.getContext("2d");
    if (!context || typeof canvas.toBlob !== "function") return file;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    let bestBlob = null;
    for (const quality of qualities) {
      const blob = await canvasToBlob(canvas, "image/jpeg", quality);
      if (!blob) continue;
      bestBlob = blob;
      if (blob.size <= targetBytes) break;
    }

    if (!bestBlob || bestBlob.size >= file.size) return file;
    return fileFromBlob(bestBlob, file);
  } catch (error) {
    console.warn("[imageCompression] Upload image compression skipped:", error.message);
    return file;
  } finally {
    if (objectUrl && typeof URL.revokeObjectURL === "function") {
      URL.revokeObjectURL(objectUrl);
    }
  }
};

export const compressImageForUploadSafe = async (file, options) => {
  try {
    return await compressImageForUpload(file, options);
  } catch {
    return file;
  }
};
